import React, { useState } from 'react';
import { WorkflowPreviewBlock, WorkflowPreviewNode, WorkflowPreviewProgress } from '../types';
import BEM from 'common/bem';

const classes = BEM.with('WorkflowPreviewPanel');

type PreviewMode = 'visual' | 'json';

const NODE_LABELS: Record<string, string> = {
  TRIGGER: 'EVENT',
  CONDITION: 'CONDITION',
  ACTION: 'ACTION',
  PRIORITY: 'PRIORITY',
  SOUND: 'SOUND',
  BUTTONS: 'BUTTONS',
  ESCALATION: 'ESCALATION',
};

function getNodeLabel(type: string) {
  return NODE_LABELS[type.toUpperCase()] || type.toUpperCase();
}

function NodeIcon({ type }: { type: string }) {
  const upper = type.toUpperCase();

  if (upper === 'TRIGGER') {
    return (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
      </svg>
    );
  }

  if (upper === 'CONDITION') {
    return (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
      </svg>
    );
  }

  if (upper === 'ACTION') {
    return (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        <circle cx="18" cy="4" r="3" fill="currentColor" stroke="none" />
      </svg>
    );
  }

  if (upper === 'PRIORITY') {
    return (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
        <line x1="4" y1="22" x2="4" y2="15" />
      </svg>
    );
  }

  if (upper === 'SOUND') {
    return (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
        <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
        <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
      </svg>
    );
  }

  if (upper === 'BUTTONS') {
    return (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5" />
        <path d="M2 12h2" />
        <path d="M12 2v2" />
        <path d="M4.93 4.93l1.41 1.41" />
        <path d="M19.07 4.93l-1.41 1.41" />
      </svg>
    );
  }

  if (upper === 'ESCALATION') {
    return (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
        <path d="M17 7l3-3-3-3" strokeWidth="2.5" />
      </svg>
    );
  }

  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

function WorkflowNodeCard({ node }: { node: WorkflowPreviewNode }) {
  const nodeType = node.type.toLowerCase();

  return (
    <div className={classes('node')}>
      <div className={classes('node-badge', { nodeType })}>{getNodeLabel(node.type)}</div>
      <div className={classes('node-card')}>
        <div className={classes('node-icon', { nodeType })}>
          <NodeIcon type={node.type} />
        </div>
        <div className={classes('node-info')}>
          <div className={classes('node-title')}>{node.title}</div>
          {node.subtitle && <div className={classes('node-subtitle')}>{node.subtitle}</div>}
        </div>
      </div>
    </div>
  );
}

function Connector() {
  return (
    <div className={classes('connector')}>
      <div className={classes('connector-line')} />
      <div className={classes('connector-arrow')} />
    </div>
  );
}

function buildDerivedJson(preview: WorkflowPreviewBlock): {
  trigger: Record<string, unknown>;
  workflow: Record<string, unknown>;
} {
  const trigger: Record<string, unknown> = {};
  const workflow: Record<string, unknown> = {};
  const escalations: Array<Record<string, unknown>> = [];

  for (const node of preview.nodes) {
    const upper = node.type.toUpperCase();
    if (upper === 'TRIGGER') {
      trigger.event = node.title;
    } else if (upper === 'CONDITION') {
      trigger.location = node.subtitle || node.title;
    } else if (upper === 'ACTION') {
      workflow.recipient = node.title;
      workflow.method = node.subtitle || 'TigerConnect Message';
    } else if (upper === 'PRIORITY') {
      workflow.priority = node.title;
    } else if (upper === 'SOUND') {
      workflow.sound = node.title;
    } else if (upper === 'BUTTONS') {
      workflow.buttons = node.subtitle ? node.subtitle.split(', ') : [];
    } else if (upper === 'ESCALATION') {
      escalations.push({ action: node.title, timer: node.subtitle || null });
    } else {
      workflow[node.type] = node.title;
    }
  }

  if (escalations.length > 0) {
    workflow.escalations = escalations;
  }

  return { trigger, workflow };
}

function JsonView({ preview }: { preview: WorkflowPreviewBlock }) {
  const hasTrigger = preview.triggerJson && Object.keys(preview.triggerJson).length > 0;
  const hasWorkflow = preview.workflowJson && Object.keys(preview.workflowJson).length > 0;

  if (hasTrigger || hasWorkflow) {
    return (
      <div className={classes('json-container')}>
        {hasTrigger && (
          <div className={classes('json-section')}>
            <div className={classes('json-label')}>Trigger JSON</div>
            <pre className={classes('json')}>{JSON.stringify(preview.triggerJson, null, 2)}</pre>
          </div>
        )}
        {hasWorkflow && (
          <div className={classes('json-section')}>
            <div className={classes('json-label')}>Workflow JSON</div>
            <pre className={classes('json')}>{JSON.stringify(preview.workflowJson, null, 2)}</pre>
          </div>
        )}
      </div>
    );
  }

  const derived = buildDerivedJson(preview);
  const hasDerivedTrigger = Object.keys(derived.trigger).length > 0;
  const hasDerivedWorkflow = Object.keys(derived.workflow).length > 0;

  return (
    <div className={classes('json-container')}>
      {hasDerivedTrigger && (
        <div className={classes('json-section')}>
          <div className={classes('json-label')}>Trigger (preview)</div>
          <pre className={classes('json')}>{JSON.stringify(derived.trigger, null, 2)}</pre>
        </div>
      )}
      {hasDerivedWorkflow && (
        <div className={classes('json-section')}>
          <div className={classes('json-label')}>Workflow (preview)</div>
          <pre className={classes('json')}>{JSON.stringify(derived.workflow, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}

function ProgressBar({ progress }: { progress: WorkflowPreviewProgress }) {
  const steps = Object.entries(progress);

  return (
    <div className={classes('progress')}>
      {steps.map(([key, status]) => (
        <div key={key} className={classes('progress-item')}>
          <span className={classes('progress-dot')} data-status={status} />
          <span className={classes('progress-label')}>
            {key.charAt(0).toUpperCase() + key.slice(1)}
          </span>
        </div>
      ))}
    </div>
  );
}

function BlankState() {
  return (
    <div className={classes('blank')}>
      <div className={classes('blank-icon')}>
        <svg
          width="48"
          height="48"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        >
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
          <line x1="3" y1="9" x2="21" y2="9" />
          <line x1="9" y1="21" x2="9" y2="9" />
        </svg>
      </div>
      <p className={classes('blank-text')}>
        Start the conversation to see your workflow preview here.
      </p>
    </div>
  );
}

type WorkflowPreviewPanelProps = {
  preview: WorkflowPreviewBlock | null;
};

export default function WorkflowPreviewPanel({ preview }: WorkflowPreviewPanelProps) {
  const [mode, setMode] = useState<PreviewMode>('visual');

  return (
    <div className={classes()}>
      <div className={classes('header')}>
        <span className={classes('header-title')}>Live Workflow Preview</span>
        <div className={classes('header-tabs')}>
          <button
            type="button"
            className={classes('header-tab', { active: mode === 'json' })}
            onClick={() => setMode('json')}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points="16 18 22 12 16 6" />
              <polyline points="8 6 2 12 8 18" />
            </svg>
            JSON
          </button>
          <button
            type="button"
            className={classes('header-tab', { active: mode === 'visual' })}
            onClick={() => setMode('visual')}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
            </svg>
            Visual
          </button>
        </div>
      </div>

      <div className={classes('body')}>
        {!preview ? (
          <BlankState />
        ) : mode === 'json' ? (
          <JsonView preview={preview} />
        ) : preview.nodes.length > 0 ? (
          <div className={classes('flow')}>
            {preview.nodes.map((node, i) => (
              <React.Fragment key={i}>
                {i > 0 && <Connector />}
                <WorkflowNodeCard node={node} />
              </React.Fragment>
            ))}
          </div>
        ) : null}
      </div>

      {preview && <ProgressBar progress={preview.progress} />}
    </div>
  );
}
